import PropTypes from 'prop-types';
import { useContext } from 'react';
import { PriceContext } from '../contexts/PriceContext';
import { UserContext } from '../contexts/UserContext';
import { useQuote } from '../hooks/useQuote';
import { gtmEventClickQuoteLink } from '../utils/analytics';
import { copyLink } from '../utils/copy-link';
import Link from './ui/Link';

export default function Quote({ children }) {
	return <div className="prc-quote">{children}</div>;
}

export function QuoteData({ consultingPackages, getLabel, plan, showPricePerUnit }) {
	const { userData, currency, locale } = useContext(UserContext);
	const { useDecimalPlaces } = useContext(PriceContext);
	const quote = useQuote({ consultingPackages, userData, plan, currency, locale, getLabel, useDecimalPlaces, showPricePerUnit });

	const getCostList = (costGroup) => {
		const headingLabelKey = costGroup === 'one-time' ? 'quote_cost_group_heading_one_time' : 'quote_cost_group_heading_recurring';

		// Return cost group if it has more than one item (the total item).
		return quote[costGroup].length > 1 ? (
			<div className={`prc-quote__cost-group prc-quote__cost-group--${costGroup}`}>
				<h6>{getLabel(headingLabelKey)}</h6>
				<ul className="prc-quote__cost-list">
					{quote[costGroup].map(([item, costs], index) => (
						<li key={index}>
							<div>{Array.isArray(item) ? item.map((partial, index) => <span key={index}>{partial}</span>) : <span>{item}</span>}</div>
							<span>{costs}</span>
						</li>
					))}
				</ul>
			</div>
		) : null;
	};

	return (
		<div className="prc-quote__data">
			<h4 className="prc-quote__heading">{getLabel('quote_heading')}</h4>
			<div className="prc-quote__table">
				{getCostList('one-time')}
				{getCostList('recurring')}
			</div>
			<div className="prc-quote__table-meta">
				<span className="prc-quote__tax-notice">{getLabel('quote_tax_notice')}</span>
				<div className="prc-quote__table-actions">
					<Link classes={['prc-quote-share prc-quote-share--copy']} onClick={() => copyLink(userData, getLabel('quote_link_copied'))}>
						{getLabel('quote_copy_link')}
					</Link>
				</div>
			</div>
		</div>
	);
}

export function QuoteContent({ generalData, getLabel }) {
	return (
		<div className="prc-quote__content">
			{generalData.quote.image ? <img src={generalData.quote.image?.full_url} alt={generalData.quote.image?.alt} /> : null}
			{generalData.quote.text ? <p className="prc-quote__text text-subline" dangerouslySetInnerHTML={{ __html: generalData.quote.text }} /> : null}
			<ul className="prc-quote__actions">
				{generalData.quote.actions.phone && (
					<li className="prc-quote__action prc-quote__action--phone">
						<Link
							href={`tel:${generalData.quote.actions.phone}`}
							onClick={() => gtmEventClickQuoteLink({ linkText: generalData.quote.actions.phone, linkUrl: `tel:${generalData.quote.actions.phone}` })}
						>
							{generalData.quote.actions.phone}
						</Link>
					</li>
				)}

				{generalData.quote.actions.email && (
					<li className="prc-quote__action prc-quote__action--email">
						<Link
							href={`mailto:${generalData.quote.actions.email}`}
							onClick={() => gtmEventClickQuoteLink({ linkText: getLabel('quote_email_link'), linkUrl: `mailto:${generalData.quote.actions.email}` })}
						>
							{getLabel('quote_email_link')}
						</Link>
					</li>
				)}
			</ul>
		</div>
	);
}

Quote.propTypes = {
	children: PropTypes.node.isRequired,
};

QuoteData.propTypes = {
	consultingPackages: PropTypes.array.isRequired,
	getLabel: PropTypes.func.isRequired,
	plan: PropTypes.object.isRequired,
	showPricePerUnit: PropTypes.bool.isRequired,
};

QuoteContent.propTypes = {
	generalData: PropTypes.object.isRequired,
	getLabel: PropTypes.func.isRequired,
};
